import { useCallback, useEffect, useState } from 'react';
import { useGetReassignUserListQuery } from '@Store/api/userApiSlice';
import { UserReplacement } from '@Types/index';
import useDebounce from '@Hooks/useDebounce';

interface ReturnValue {
  reassignUsers: UserReplacement[] | undefined;
  onLoadMore: () => void;
  onSearch: (searchValue: string) => void;
}

interface UseReassignManagerProps {
  empNumber: string;
  creatorRequestNumber: string;
}

const useReassignUsersManager = ({
  empNumber,
  creatorRequestNumber,
}: UseReassignManagerProps): ReturnValue => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [searchValue, setSearchValue] = useState('');
  const searchValueQuery = useDebounce(searchValue, 300);
  const { data: reassignUsers } = useGetReassignUserListQuery(
    {
      page: currentPage,
      pageSize,
      searchValue: String(searchValueQuery),
      excludeEmpNumbers: [empNumber, creatorRequestNumber],
    },
    {
      skip: !empNumber || !creatorRequestNumber,
      refetchOnMountOrArgChange: true,
    }
  );
  const [loadedReassignUsers, setLoadedReassignUsers] = useState<
    UserReplacement[]
  >([]);

  const handleLoadMore = (): void => {
    setPageSize(9);
    setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    if (reassignUsers) {
      setLoadedReassignUsers([...loadedReassignUsers, ...reassignUsers]);
    }
  }, [reassignUsers]);

  const handleSearch = useCallback((searchQuery: string): void => {
    setLoadedReassignUsers([]);
    setSearchValue(searchQuery);
    setPageSize(20);
  }, []);

  return {
    reassignUsers: loadedReassignUsers.length
      ? loadedReassignUsers
      : reassignUsers,
    onLoadMore: handleLoadMore,
    onSearch: handleSearch,
  };
};

export default useReassignUsersManager;
